import { useState, useMemo, useEffect } from 'react';
import { copyToClipboard } from '@/lib/utils';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  useGroup,
  useGroupMembers,
  useCheckActiveLoans,
  useLeaveGroup,
  ActiveLoanInfo,
} from '@/hooks/useGroups';
import { useGroupItems, useDeleteItem, ItemWithOwner } from '@/hooks/useItems';
import {
  useInitiateReturn,
  useConfirmReturn,
  useCancelReturn,
  useAcceptLoanProposal,
  useCancelLoanProposal,
} from '@/hooks/useLoans';
import { AddItemsToGroupDialog } from '@/components/AddItemsToGroupDialog';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Package,
  Link2,
  FolderPlus,
  LogOut,
  Search,
  X,
  ArrowLeft,
  ChevronRight,
} from 'lucide-react';
import { LoanRequestsList } from '@/components/LoanRequestsList';
import { MyLoanRequests } from '@/components/MyLoanRequests';
import { useGroupInvites, useGenerateInvite, useRevokeInvite } from '@/hooks/useGroupInvites';
import { findItemCategoryByText } from '@/lib/itemCategoryConfig';
import { AddItemDialog } from '@/components/AddItemDialog';
import { EditItemDialog } from '@/components/EditItemDialog';
import { T, RADIUS, SHADOW } from '@/lib/tokens';
import posthog from '@/lib/posthog';
import { FilterTabStrip } from '@/components/design/FilterTabStrip';
import { ItemGrid } from '@/components/design/ItemGrid';
import { ItemDetailPanel } from '@/components/design/ItemDetailPanel';
import { Layout } from '@/components/design/Layout';
import { ActivityFeedBand } from '@/components/design/ActivityFeedBand';
import { InvitePanel } from '@/components/InvitePanel';
import { AddItemsNudgeBanner } from '@/components/AddItemsNudgeBanner';
import { InviteReminderBanner } from '@/components/InviteReminderBanner';
import { InvitedUserWelcome } from '@/components/InvitedUserWelcome';
import { CoachMark, CoachMarkStep } from '@/components/CoachMark';
import { LendItemDialog } from '@/components/LendItemDialog';
import { ConfirmReturnDialog } from '@/components/ConfirmReturnDialog';

const CATALOGUE_TABS = ['All', 'Available', 'Borrowed', 'Mine'];

/**
 *
 */
export default function GroupView() {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: group, isLoading: groupLoading, isError: groupError } = useGroup(groupId!);
  const { data: members } = useGroupMembers(groupId!);
  const {
    data: itemsData,
    isLoading: itemsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGroupItems(groupId!);
  const items = useMemo(() => itemsData?.pages.flatMap((p) => p.items), [itemsData]);

  const deleteItem = useDeleteItem();
  const initiateReturn = useInitiateReturn();
  const confirmReturn = useConfirmReturn();
  const cancelReturn = useCancelReturn();
  const acceptLoanProposal = useAcceptLoanProposal();
  const cancelLoanProposal = useCancelLoanProposal();
  const checkActiveLoans = useCheckActiveLoans();
  const leaveGroup = useLeaveGroup();
  const { data: invites } = useGroupInvites(groupId!);
  const generateInvite = useGenerateInvite();
  const revokeInvite = useRevokeInvite();

  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [showNewMemberWelcome, setShowNewMemberWelcome] = useState(false);
  const [welcomeHasItem, setWelcomeHasItem] = useState(false);
  const [coachMarkStep, setCoachMarkStep] = useState<number | null>(null);

  // Remove ?item= and ?newMember= after reading to avoid re-showing on refresh
  useEffect(() => {
    const itemParam = searchParams.get('item');
    const newMemberParam = searchParams.get('newMember');

    if (itemParam) {
      setSelectedItemId(itemParam);
    }
    if (newMemberParam === 'true' && groupId) {
      // Only show welcome if not already seen for this group
      const seenKey = `inviteWelcomeSeen_${groupId}`;
      if (!localStorage.getItem(seenKey)) {
        // Write immediately so the overlay is not shown again if the user exits without dismissing
        localStorage.setItem(seenKey, 'true');
        setShowNewMemberWelcome(true);
        setWelcomeHasItem(!!itemParam);
        posthog.capture('invite_welcome_shown', { group_id: groupId });
      }
    }
    if (itemParam || newMemberParam) {
      const next = new URLSearchParams(searchParams);
      next.delete('item');
      next.delete('newMember');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, groupId]);
  const [activeTab, setActiveTab] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const [addItemOpen, setAddItemOpen] = useState(false);
  const [editItemOpen, setEditItemOpen] = useState(false);
  const [lendDialogOpen, setLendDialogOpen] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [activeLoansDialogOpen, setActiveLoansDialogOpen] = useState(false);
  const [activeLoans, setActiveLoans] = useState<ActiveLoanInfo[]>([]);
  const [selectedItem, setSelectedItem] = useState<ItemWithOwner | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<ItemWithOwner | null>(null);
  const [invitePanelOpen, setInvitePanelOpen] = useState(false);
  const [addItemsToGroupOpen, setAddItemsToGroupOpen] = useState(false);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnDialogLoanId, setReturnDialogLoanId] = useState<string | null>(null);
  const [returnDialogItemName, setReturnDialogItemName] = useState('');
  const [returnDialogMode, setReturnDialogMode] = useState<'initiate' | 'confirm'>('initiate');

  const handleOpenInvitePanel = () => {
    setInvitePanelOpen(true);
  };

  const openDeleteConfirm = (item: ItemWithOwner) => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteItem = async () => {
    if (!itemToDelete) return;
    try {
      await deleteItem.mutateAsync({ id: itemToDelete.id });
      toast.success('Item deleted');
    } catch {
      // Error toast handled by useDeleteItem onError
    }
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  const handleInitiateReturn = async (item: ItemWithOwner) => {
    if (!item.active_loan) return;
    // Borrower gets the condition dialog; owner calls mutation directly
    if (user && item.active_loan.borrower_id === user.id) {
      setReturnDialogLoanId(item.active_loan.id);
      setReturnDialogItemName(item.name);
      setReturnDialogMode('initiate');
      setReturnDialogOpen(true);
      return;
    }
    try {
      await initiateReturn.mutateAsync({ loanId: item.active_loan.id });
      toast.success('Return initiated — waiting for confirmation');
    } catch {
      // Error toast handled by useInitiateReturn onError
    }
  };

  const handleConfirmReturn = async (item: ItemWithOwner) => {
    if (!item.active_loan) return;
    // Borrower gets the condition dialog; owner calls mutation directly
    if (user && item.active_loan.borrower_id === user.id) {
      setReturnDialogLoanId(item.active_loan.id);
      setReturnDialogItemName(item.name);
      setReturnDialogMode('confirm');
      setReturnDialogOpen(true);
      return;
    }
    try {
      await confirmReturn.mutateAsync({ loanId: item.active_loan.id });
      toast.success('Return confirmed!');
      setSelectedItemId(null);
    } catch {
      // Error toast handled by useConfirmReturn onError
    }
  };

  const handleCancelReturn = async (item: ItemWithOwner) => {
    if (!item.active_loan) return;
    try {
      await cancelReturn.mutateAsync({ loanId: item.active_loan.id });
      toast.success('Return cancelled');
    } catch {
      // Error toast handled by useCancelReturn onError
    }
  };

  const handleAcceptLoanProposal = async (item: ItemWithOwner) => {
    if (!item.active_loan) return;
    try {
      await acceptLoanProposal.mutateAsync({ loanId: item.active_loan.id });
      toast.success('Loan accepted!');
    } catch {
      // Error toast handled by useAcceptLoanProposal onError
    }
  };

  const handleCancelLoanProposal = async (item: ItemWithOwner) => {
    if (!item.active_loan) return;
    try {
      await cancelLoanProposal.mutateAsync({ loanId: item.active_loan.id });
      toast.success('Loan proposal cancelled');
    } catch {
      // Error toast handled by useCancelLoanProposal onError
    }
  };

  const openLendDialog = (item: ItemWithOwner) => {
    setSelectedItem(item);
    setLendDialogOpen(true);
  };

  const openEditDialog = (item: ItemWithOwner) => {
    setSelectedItem(item);
    setEditItemOpen(true);
  };

  const handleLeaveGroup = async () => {
    try {
      const loans = await checkActiveLoans.mutateAsync(groupId!);
      if (loans.length > 0) {
        setActiveLoans(loans);
        setActiveLoansDialogOpen(true);
        return;
      }
      setLeaveDialogOpen(true);
    } catch {
      // Error toast handled by useCheckActiveLoans onError
    }
  };

  const confirmLeaveGroup = async () => {
    try {
      await leaveGroup.mutateAsync(groupId!);
      toast.success('You have left the group');
      navigate('/dashboard');
    } catch {
      // Error toast handled by useLeaveGroup onError
    }
    setLeaveDialogOpen(false);
  };

  const totalItems = items?.length ?? 0;
  const onLoanCount = items?.filter((i) => !!i.active_loan).length ?? 0;
  const memberCount = members?.length ?? 0;
  const userItemsInGroup = items?.filter((i) => i.owner_id === user!.id).length ?? 0;
  const isGroupCreator = group?.created_by === user?.id;

  // Brand is searched only for TOOL items because brand is only stored/relevant
  // for tools (non-tool categories store brand in the detail table, not items).
  const filteredItems = items?.filter((item) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const cat = item.category ? findItemCategoryByText(item.category) : undefined;
      if (
        !item.name.toLowerCase().includes(q) &&
        !item.description?.toLowerCase().includes(q) &&
        !(cat === 'TOOL' && item.brand?.toLowerCase().includes(q))
      )
        return false;
    }
    if (activeTab === 'Available') return !item.active_loan;
    if (activeTab === 'Borrowed') return item.active_loan?.borrower_id === user!.id;
    if (activeTab === 'Mine') return item.owner_id === user!.id;
    return true;
  });

  // Sort: giving-away items float to the top within the current filter set.
  // Client-side sort on loaded items — modifying the DB sort would break cursor
  // pagination (which is keyed on created_at).
  const sortedItems = [...(filteredItems ?? [])].sort((a, b) => {
    if (a.is_giving_away === b.is_giving_away) return 0;
    return a.is_giving_away ? -1 : 1;
  });

  const selectedItemData = selectedItemId
    ? (items?.find((i) => i.id === selectedItemId) ?? null)
    : null;

  if (groupLoading) {
    return (
      <Layout title="Loading…">
        <div style={{ textAlign: 'center', color: T.textMuted, padding: '64px 0', fontSize: 14 }}>
          Loading…
        </div>
      </Layout>
    );
  }

  if (groupError || !group) {
    return (
      <Layout title="Group">
        <div style={{ textAlign: 'center', padding: '64px 24px' }}>
          <p style={{ fontSize: 15, fontWeight: 600, color: T.danger, margin: '0 0 6px' }}>
            {groupError ? 'Failed to load group' : 'Group not found'}
          </p>
          <p style={{ fontSize: 13, color: T.textMuted, margin: 0 }}>
            Please check your connection and try again.
          </p>
        </div>
      </Layout>
    );
  }

  const LendDialog = (
    <LendItemDialog
      open={lendDialogOpen}
      onOpenChange={setLendDialogOpen}
      item={selectedItem}
      members={members ?? []}
      currentUserId={user!.id}
      groupId={groupId}
    />
  );

  if (selectedItemData) {
    const backArrow = (
      <button
        onClick={() => setSelectedItemId(null)}
        aria-label="Back"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: T.text,
          padding: 0,
          width: 44,
          height: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ArrowLeft size={22} />
      </button>
    );

    return (
      <>
        <Layout
          title={selectedItemData.name}
          titleStyle={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          leftAction={backArrow}
        >
          <ItemDetailPanel
            item={selectedItemData}
            currentUserId={user!.id}
            onBack={() => setSelectedItemId(null)}
            onInitiateReturn={handleInitiateReturn}
            onConfirmReturn={handleConfirmReturn}
            onCancelReturn={handleCancelReturn}
            onLend={openLendDialog}
            onEdit={openEditDialog}
            onDelete={openDeleteConfirm}
            onAcceptProposal={handleAcceptLoanProposal}
            onCancelProposal={handleCancelLoanProposal}
            groupId={groupId}
          />
        </Layout>

        <EditItemDialog open={editItemOpen} onOpenChange={setEditItemOpen} item={selectedItem} />
        {LendDialog}
        <ConfirmReturnDialog
          loanId={returnDialogLoanId}
          itemName={returnDialogItemName}
          mode={returnDialogMode}
          open={returnDialogOpen}
          onOpenChange={setReturnDialogOpen}
        />
        <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                {itemToDelete?.active_loan
                  ? 'This item is currently on loan and cannot be deleted. Please return it first.'
                  : `This will permanently delete "${itemToDelete?.name}". This action cannot be undone.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              {!itemToDelete?.active_loan && (
                <AlertDialogAction onClick={handleDeleteItem}>Delete</AlertDialogAction>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  const headerContent = (
    <>
      {/* Stats strip */}
      <div
        style={{
          display: 'flex',
          marginBottom: 14,
          background: T.surface,
          border: `1px solid ${T.border}`,
          borderRadius: RADIUS.lg,
          boxShadow: SHADOW.md,
          overflow: 'hidden',
        }}
      >
        {/* Items stat */}
        <div
          style={{
            flex: 1,
            padding: '12px 0',
            textAlign: 'center',
            borderRight: `1px solid ${T.border}`,
          }}
        >
          <div style={{ fontSize: 26, color: T.text, fontWeight: 600, letterSpacing: '-0.03em' }}>
            {totalItems}
          </div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: T.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.07em',
              marginTop: 1,
            }}
          >
            Items
          </div>
        </div>
        {/* On loan stat */}
        <div
          style={{
            flex: 1,
            padding: '12px 0',
            textAlign: 'center',
            borderRight: `1px solid ${T.border}`,
          }}
        >
          <div style={{ fontSize: 26, color: T.accent, fontWeight: 600, letterSpacing: '-0.03em' }}>
            {onLoanCount}
          </div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: T.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.07em',
              marginTop: 1,
            }}
          >
            On loan
          </div>
        </div>
        {/* Members stat — clickable, navigates to members page */}
        <button
          type="button"
          onClick={() => navigate(`/groups/${groupId}/members`)}
          style={{
            flex: 1,
            padding: '12px 0',
            textAlign: 'center',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            font: 'inherit',
            position: 'relative',
          }}
        >
          <div style={{ fontSize: 26, color: T.text, fontWeight: 600, letterSpacing: '-0.03em' }}>
            {memberCount}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
              marginTop: 1,
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: T.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.07em',
              }}
            >
              Members
            </span>
            <ChevronRight size={11} style={{ color: T.textMuted }} />
          </div>
        </button>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <button
          onClick={() => setAddItemOpen(true)}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            padding: '9px 8px',
            borderRadius: RADIUS.sm,
            border: 'none',
            background: T.accent,
            cursor: 'pointer',
            color: T.white,
            fontSize: 13,
            fontWeight: 600,
            fontFamily: 'inherit',
            boxShadow: SHADOW.btn.accent,
          }}
        >
          <FolderPlus size={14} />
          Add Items
        </button>
        <button
          onClick={handleOpenInvitePanel}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            padding: '9px 8px',
            borderRadius: RADIUS.sm,
            border: `1px solid ${T.border}`,
            background: T.surface,
            cursor: 'pointer',
            color: T.textSec,
            fontSize: 13,
            fontWeight: 600,
            fontFamily: 'inherit',
          }}
        >
          <Link2 size={14} />
          Invite
        </button>
        <button
          onClick={handleLeaveGroup}
          disabled={checkActiveLoans.isPending}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            padding: '9px 8px',
            borderRadius: RADIUS.sm,
            border: `1px solid ${T.border}`,
            background: T.surface,
            cursor: checkActiveLoans.isPending ? 'default' : 'pointer',
            color: T.textSec,
            fontSize: 13,
            fontWeight: 600,
            fontFamily: 'inherit',
            opacity: checkActiveLoans.isPending ? 0.7 : 1,
          }}
        >
          <LogOut size={14} />
          {checkActiveLoans.isPending ? 'Checking…' : 'Leave'}
        </button>
      </div>

      {/* Search bar */}
      <div
        style={{
          background: T.surfaceSubtle,
          border: `1px solid ${T.border}`,
          borderRadius: RADIUS.md,
          boxShadow: SHADOW.sm,
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 14,
        }}
      >
        <Search size={14} color={T.textMuted} />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search items, owners…"
          style={{
            flex: 1,
            border: 'none',
            background: 'transparent',
            outline: 'none',
            color: searchQuery ? T.text : T.textMuted,
            fontSize: 14,
            fontFamily: 'inherit',
          }}
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            aria-label="Clear search"
            style={{
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              color: T.textMuted,
              padding: 0,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <X size={14} />
          </button>
        )}
      </div>

      <ActivityFeedBand groupId={groupId!} onClick={() => navigate(`/groups/${groupId}/activity`)} />

      {/* Filter tab strip */}
      <FilterTabStrip tabs={CATALOGUE_TABS} activeTab={activeTab} onTabChange={setActiveTab} />
    </>
  );

  const backArrow = (
    <button
      onClick={() => navigate('/dashboard')}
      aria-label="Back to groups"
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: T.text,
        padding: 0,
        width: 44,
        height: 44,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <ArrowLeft size={22} />
    </button>
  );

  const handleDismissWelcome = () => {
    setShowNewMemberWelcome(false);
    posthog.capture('invite_welcome_dismissed', { group_id: groupId });
    // Start coach marks, but skip if user has a specific item open (they know what they're doing)
    if (groupId && !welcomeHasItem) {
      const marksKey = `coachMarksDone_${groupId}`;
      if (!localStorage.getItem(marksKey)) {
        setCoachMarkStep(0);
      }
    }
  };

  const coachMarkSteps: CoachMarkStep[] = [
    {
      title: 'Browse what\'s available',
      body: 'Tap any item card to see details and request to borrow it from the owner.',
    },
    {
      title: 'Share something back',
      body: 'Tap "+ Add Items" to list things you\'re happy for others to borrow.',
    },
  ];

  const handleCoachMarkNext = () => {
    if (coachMarkStep !== null && coachMarkStep < coachMarkSteps.length - 1) {
      setCoachMarkStep(coachMarkStep + 1);
    }
  };

  const handleCoachMarkDismiss = () => {
    setCoachMarkStep(null);
    posthog.capture('coach_mark_dismissed', { group_id: groupId, step: coachMarkStep });
    if (groupId) {
      localStorage.setItem(`coachMarksDone_${groupId}`, 'true');
    }
  };

  return (
    <>
      {showNewMemberWelcome && (
        <InvitedUserWelcome
          groupName={group.name}
          hasItemHighlighted={welcomeHasItem}
          onDismiss={handleDismissWelcome}
        />
      )}
      {!showNewMemberWelcome && coachMarkStep !== null && (
        <CoachMark
          step={coachMarkSteps[coachMarkStep]}
          stepIndex={coachMarkStep}
          totalSteps={coachMarkSteps.length}
          onNext={handleCoachMarkNext}
          onDismiss={handleCoachMarkDismiss}
          position={coachMarkStep === 1 ? 'top' : 'bottom'}
        />
      )}
      <Layout title={group.name} leftAction={backArrow} headerContent={headerContent}>
        {/* Post-onboarding nudge banners */}
        <InviteReminderBanner groupId={groupId!} memberCount={memberCount} />
        <AddItemsNudgeBanner
          onAddItem={() => setAddItemOpen(true)}
          userItemCount={userItemsInGroup}
          userId={user?.id || ''}
        />

        {/* Loan panels */}
        <LoanRequestsList />
        <MyLoanRequests />

        {/* Item list */}
        {itemsLoading ? (
          <div style={{ textAlign: 'center', color: T.textMuted, padding: '32px 0', fontSize: 14 }}>
            Loading items…
          </div>
        ) : sortedItems.length === 0 ? (
          <div style={{ textAlign: 'center', color: T.textMuted, padding: '48px 0' }}>
            <div
              style={{ display: 'flex', justifyContent: 'center', marginBottom: 12, opacity: 0.3 }}
            >
              <Package size={40} color={T.textMuted} />
            </div>
            <p style={{ fontSize: 15, fontWeight: 600, color: T.textSec, margin: '0 0 6px' }}>
              No items yet
            </p>
            <p style={{ fontSize: 13, margin: 0 }}>Be the first to add an item to share!</p>
          </div>
        ) : (
          <>
            {sortedItems[0]?.is_giving_away && (
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  color: T.textMuted,
                  marginBottom: 8,
                }}
              >
                Being Given Away
              </div>
            )}
            <ItemGrid
              items={sortedItems}
              currentUserId={user!.id}
              onItemClick={(id) => setSelectedItemId(id)}
              groupId={groupId}
            />
            {hasNextPage && (
              <div style={{ textAlign: 'center', padding: '12px 0' }}>
                <button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  style={{
                    border: `1px solid ${T.border}`,
                    background: T.surfaceSubtle,
                    borderRadius: RADIUS.sm,
                    padding: '8px 20px',
                    cursor: isFetchingNextPage ? 'default' : 'pointer',
                    color: T.textSec,
                    fontSize: 13,
                    fontFamily: 'inherit',
                  }}
                >
                  {isFetchingNextPage ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </Layout>

      {/* Dialogs */}
      <AddItemDialog
        open={addItemOpen}
        onOpenChange={setAddItemOpen}
        groupId={groupId!}
        onOpenExistingItems={() => setAddItemsToGroupOpen(true)}
      />
      <AddItemsToGroupDialog
        groupId={groupId!}
        open={addItemsToGroupOpen}
        onOpenChange={setAddItemsToGroupOpen}
      />
      <InvitePanel open={invitePanelOpen} onOpenChange={setInvitePanelOpen} groupId={groupId!} />
      <EditItemDialog open={editItemOpen} onOpenChange={setEditItemOpen} item={selectedItem} />
      {LendDialog}

      <AlertDialog open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave "{group.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Your items will be removed from this group. If you're the last member, the group will
              be deleted. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmLeaveGroup} disabled={leaveGroup.isPending}>
              {leaveGroup.isPending ? 'Leaving...' : 'Leave Group'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={activeLoansDialogOpen} onOpenChange={setActiveLoansDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cannot Leave Group</DialogTitle>
            <DialogDescription>
              You have active loans that must be returned before you can leave this group.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {activeLoans.map((loan) => (
              <div key={loan.loan_id} className="rounded-md border p-3 space-y-1">
                <p className="font-medium">{loan.item_name}</p>
                <p className="text-sm text-muted-foreground">
                  {loan.role === 'owner'
                    ? 'You own this item and it is currently lent out'
                    : 'You are currently borrowing this item'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Since {new Date(loan.borrowed_at).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
          <p className="text-sm text-muted-foreground">
            Please return all items before leaving the group.
          </p>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              {itemToDelete?.active_loan
                ? 'This item is currently on loan and cannot be deleted. Please return it first.'
                : `This will permanently delete "${itemToDelete?.name}". This action cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            {!itemToDelete?.active_loan && (
              <AlertDialogAction onClick={handleDeleteItem}>Delete</AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
